import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { CardRow, Cell, H4, Row, Section, Status, StatusPayment, TableTimestamp, Tag, TooltipIcon, TooltipWrapper, User } from 'components';
import { Loadable } from 'components/types';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { AchMethod, AchResponseType, AchStatus, IAchTransaction, IAlert, IPayout, PayoutStatus } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { formatDateForUI, getTimeWithZone } from 'utils/date';
import { capitalize } from 'utils/strings';

export const CardRowWrapper = styled(CardRow)`
  margin-top: ${toRem(-theme.card.minHeight.small)};

  @media (max-width: ${theme.breakpoint.small}px) {
    margin-top: 0;
  }
`;

// RTH = ReportTableHeader
const RTHLayout = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(22)};
  gap: ${toRem(12)};
`;

const RTHTitleLayout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: baseline;
  width: auto;

  ${H4} {
    strong {
      font-weight: ${theme.font.weight.semiBold};
    }
  }
`;

const RTHActions = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  gap: ${toRem(11)};
`;

interface IReportTableHeaderProps extends Loadable {
  actions?: ReactNode;
  count?: number;
  title: string;
}

export const ReportTableHeader = ({ actions, count, isLoading, title = 'Results' }: IReportTableHeaderProps) => (
  <RTHLayout>
    <RTHTitleLayout data-cy="report-table-header">
      <H4>
        {isLoading || isNil(count) ? (
          <Skeleton width={55} />
        ) : (
          <>
            <strong>{title}</strong> {` (${count || 0})`}
          </>
        )}
      </H4>
    </RTHTitleLayout>

    {actions && <RTHActions>{actions}</RTHActions>}
  </RTHLayout>
);

interface IPaidPaymentsRowProps extends Loadable {
  payout?: IPayout;
  onClick?: () => void;
}

export const PaidPaymentsRow = ({ payout, isLoading, onClick }: IPaidPaymentsRowProps) => {
  const submissionDate = (payout?.statusHistory || []).find((s) => s.action === 'create')?.createdAt;
  const submissionDateFormatted = submissionDate && formatDateForUI(submissionDate);
  const submissionTime = submissionDate && getTimeWithZone(submissionDate);

  return (
    <Row clickable onClick={onClick}>
      <Cell data-cy="payee-name">
        <User hasPaymentMethod={false} id={payout?.userId} userId={payout?.userId} isLoading={isLoading} username={payout?.employee.name} />
      </Cell>

      <Cell minWidth={110} data-cy="payout-id">
        {isLoading ? <Skeleton width={188} /> : payout?.id}
      </Cell>

      <Cell minWidth={160} data-cy="business-day">
        {isLoading ? (
          <Skeleton width={160} />
        ) : (
          <TooltipWrapper data-cy="tooltip-wrapper">
            {`${payout?.paymentDate} `}
            <TooltipIcon id={`tooltip-${payout?.id}`}>
              <p>
                <strong>Date Submitted</strong>
                <br />
                {submissionDateFormatted}
                <br />
                {submissionTime}
              </p>
            </TooltipIcon>
          </TooltipWrapper>
        )}
      </Cell>

      <Cell align="right" minWidth={88} data-cy="gross">
        {isLoading ? <Skeleton width={88} /> : centsToDollarsDisplay(payout?.gross)}
      </Cell>

      <Cell align="right" minWidth={88} data-cy="holdback">
        {isLoading ? <Skeleton width={88} /> : centsToNegativeDollarsDisplay(payout?.holdback)}
      </Cell>

      <Cell align="right" minWidth={88} data-cy="net">
        {isLoading ? <Skeleton width={88} /> : <strong>{centsToDollarsDisplay(payout?.net)}</strong>}
      </Cell>

      <Cell minWidth={88} data-cy="disbursement">
        {isLoading ? <Skeleton width={88} /> : capitalize(payout?.disbursementMethod)}
      </Cell>
    </Row>
  );
};

// Payroll Tips
export const PayrollPeriodWrapper = styled.div`
  display: flex;
  width: ${toRem(210)};

  .react-dropdown-select-dropdown {
    white-space: nowrap;
  }
`;

interface IPayrollTipsRowProps extends Loadable {
  employeeId: string;
  gross: string;
  holdback?: string;
  total: string;
  userHasPaymentMethod: boolean;
  userId: string;
  userImage?: string;
  userName: string;
  instant: string;
  payroll: string;
  autoGratuity: string;
  tips: string;
  statusCounts: { paid: number; payroll: number; pending: number; canceled: number; expired: number };
  onClick: () => void;
}

const PSRContentLayout = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-gap: ${toRem(5.5)};
  width: 100%;

  > * {
    margin-bottom: 0;
    line-height: 100%;
    color: ${theme.color.neutral[900]};
  }
`;

export const PayrollTipsRow = ({ employeeId, gross, holdback, isLoading, total, userHasPaymentMethod, userId, userImage, userName, instant, payroll, autoGratuity, tips, statusCounts, onClick, ...rest }: IPayrollTipsRowProps) => (
  <Row clickable onClick={onClick} {...rest}>
    <Cell data-cy="payee-name">
      <User hasPaymentMethod={userHasPaymentMethod} id={userId} image={userImage} isLoading={isLoading} username={userName} details={employeeId} />
    </Cell>

    <Cell data-cy="status">
      {isLoading ? (
        <Skeleton width={55} />
      ) : (
        <PSRContentLayout>
          {statusCounts.paid !== 0 && !isLoading && <Status data-cy="status-paid" appearance={Appearance.SUCCESS} label={`Paid (${statusCounts.paid})`} isLoading={isLoading} />}
          {statusCounts.pending !== 0 && !isLoading && <Status data-cy="status-pending" appearance={Appearance.WARNING} label={`Pending (${statusCounts.pending})`} isLoading={isLoading} />}
          {statusCounts.expired !== 0 && !isLoading && <Status data-cy="status-expired" appearance={Appearance.DANGER} label={`Expired (${statusCounts.expired})`} isLoading={isLoading} />}
          {statusCounts.canceled !== 0 && !isLoading && <Status data-cy="status-canceled" appearance={Appearance.NEUTRAL} label={`Canceled (${statusCounts.canceled})`} isLoading={isLoading} />}
          {statusCounts.payroll !== 0 && !isLoading && <Status data-cy="status-payroll" appearance={Appearance.NEUTRAL} label={`Payroll (${statusCounts.payroll})`} isLoading={isLoading} />}
        </PSRContentLayout>
      )}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="tips">
      {isLoading ? <Skeleton width={88} /> : tips}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="autograts">
      {isLoading ? <Skeleton width={88} /> : autoGratuity}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="gross">
      {isLoading ? <Skeleton width={88} /> : gross}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="holdback">
      {isLoading ? <Skeleton width={88} /> : holdback}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="instant">
      {isLoading ? <Skeleton width={88} /> : instant}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="payroll">
      {isLoading ? <Skeleton width={88} /> : payroll}
    </Cell>

    <Cell align="left" minWidth={88} data-cy="total">
      {isLoading ? <Skeleton width={88} /> : <strong>{total}</strong>}
    </Cell>
  </Row>
);

// Bank Reconciliation
export const BRSection = styled(Section)`
  z-index: 10;
  padding-bottom: 0;

  > div {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const BRSummaryRow = styled.div`
  display: grid;
  width: 100%;
  grid-gap: ${toRem(22)};
  grid-template-columns: 1fr;
  padding: ${toRem(22)} ${toRem(22)};

  @media (min-width: ${theme.breakpoint.small}px) {
    grid-template-columns: repeat(auto-fit, minmax(${toRem(200)}, 1fr));
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(22)} 0;
    min-height: ${toRem(110)};
    padding: ${toRem(22)} 0;
  }
`;

const BRCardLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  padding-right: ${toRem(22)};

  @media (min-width: ${theme.breakpoint.med}px) {
    border-right: ${toRem(1)} solid ${theme.color.neutral[200]};

    &:last-child {
      border-right: none;
    }
  }
`;

const BRCardInner = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    justify-content: center;
  }
`;

interface IBRCardProps {
  children: ReactNode;
}

export const BRCard = ({ children }: IBRCardProps) => (
  <BRCardLayout>
    <BRCardInner>{children}</BRCardInner>
  </BRCardLayout>
);

export const BRMultiTableLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(22)};
  width: 100%;
  height: auto;
  padding: ${toRem(22)};
`;

export const BRTableSection = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)};
  width: 100%;
`;

export const BRTableHeader = styled.header`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  ${H4} {
    display: flex;

    strong {
      font-weight: ${theme.font.weight.medium};
    }
  }
`;

interface IDailyReportRowProps extends Loadable {
  trxId?: string;
  amount?: string;
  date?: string;
}

export const DailyReportRow = ({ amount, date, isLoading, ...rest }: IDailyReportRowProps) => (
  <Row {...rest}>
    <Cell data-cy="date">{isLoading ? <Skeleton width={77} /> : date}</Cell>

    <Cell align="right" data-cy="amount">
      {isLoading ? <Skeleton width={77} /> : <strong>{amount}</strong>}
    </Cell>
  </Row>
);

interface IDetailDebitReportRowProps extends IDailyReportRowProps {
  description?: string;
  type?: string;
  employeeName?: string;
  transactionId?: string;
}

export const DetailDebitReportRow = ({ amount, date, description, employeeName, transactionId, isLoading, type, ...rest }: IDetailDebitReportRowProps) => (
  <Row {...rest}>
    <Cell minWidth={77} data-cy="date">
      {isLoading ? <Skeleton width={77} /> : date}
    </Cell>

    <Cell minWidth={77} data-cy="type">
      {isLoading ? <Skeleton width={77} /> : type}
    </Cell>

    <Cell minWidth={200} maxWidth={200} wrapText data-cy="description">
      {isLoading ? <Skeleton width={200} /> : description || '--'}
    </Cell>

    <Cell minWidth={200} maxWidth={200} wrapText data-cy="employeeName">
      {isLoading ? <Skeleton width={200} /> : employeeName || '--'}
    </Cell>

    <Cell minWidth={200} maxWidth={200} wrapText data-cy="transactionId">
      {isLoading ? <Skeleton width={200} /> : transactionId || '--'}
    </Cell>

    <Cell minWidth={77} align="right" data-cy="amount">
      {isLoading ? <Skeleton width={77} /> : <strong>{amount}</strong>}
    </Cell>
  </Row>
);

interface IDetailCreditReportRowProps extends IDailyReportRowProps {
  description?: string;
  notes?: string;
  type?: string;
}

export const DetailCreditReportRow = ({ trxId, amount, date, description, notes, isLoading, type, ...rest }: IDetailCreditReportRowProps) => (
  <Row {...rest}>
    <Cell minWidth={77} data-cy="date">
      {isLoading ? <Skeleton width={77} /> : date}
    </Cell>

    <Cell minWidth={77} data-cy="type">
      {isLoading ? <Skeleton width={77} /> : type}
    </Cell>

    <Cell minWidth={200} maxWidth={200} wrapText data-cy="description">
      {isLoading ? (
        <Skeleton width={200} />
      ) : (
        (
          <>
            {description}
            {notes && (
              <TooltipIcon id={trxId} iconSize={13.25} iconMarginLeft={3.75} place="top">
                {notes}
              </TooltipIcon>
            )}
          </>
        ) || '--'
      )}
    </Cell>

    <Cell minWidth={77} align="right" data-cy="amount">
      {isLoading ? <Skeleton width={77} /> : <strong>{amount}</strong>}
    </Cell>
  </Row>
);

// Ledger
interface ILedgerRowProps extends Loadable {
  trxId?: string;
  amount?: string;
  date?: string;
  description?: string;
  notes?: string;
  newBalance?: string;
  relatedId?: string;
  time?: string;
  type?: string;
  employeeName?: string;
}

export const LedgerRow = ({ trxId, amount, date, description, notes, isLoading, newBalance, relatedId, time, type, employeeName, ...rest }: ILedgerRowProps) => (
  <Row {...rest}>
    <Cell data-cy="timestamp">{date && <TableTimestamp align="left" date={date} time={time} isLoading={isLoading} />}</Cell>

    <Cell minWidth={150} data-cy="type">
      {isLoading ? <Skeleton width={150} /> : type ? capitalize(type.replaceAll('_', ' ')) : ''}
    </Cell>

    <Cell minWidth={300} maxWidth={350} wrapText data-cy="description">
      {isLoading ? (
        <Skeleton width={300} />
      ) : (
        <>
          {description || '--'}
          {notes && (
            <TooltipIcon id={trxId} iconSize={13.25} iconMarginLeft={3.75} place="top">
              {notes}
            </TooltipIcon>
          )}
        </>
      )}
    </Cell>

    <Cell align="left" data-cy="employee-name">
      {isLoading ? <Skeleton width={55} /> : employeeName || '--'}
    </Cell>

    <Cell align="left" data-cy="related-id">
      {isLoading ? <Skeleton width={55} /> : relatedId || '--'}
    </Cell>

    <Cell minWidth={100} align="right" data-cy="amount">
      <strong>{isLoading ? <Skeleton width={100} /> : amount}</strong>
    </Cell>

    <Cell minWidth={100} align="right" data-cy="new-balance">
      <strong>{isLoading ? <Skeleton width={100} /> : newBalance}</strong>
    </Cell>
  </Row>
);

const StyledSkeleton = styled(Skeleton)`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 100%;
  text-align: right;

  span {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    text-align: right;
  }
`;

const Column = styled.div`
  flex-flow: column;
  width: 100%;
  gap: ${toRem(2)};
`;

interface IUserPayoutRowProps {
  gross: string;
  holdback: string;
  net: string;
  autograts: string;
  tips: string;
  alert?: IAlert;
  businessDay: string;
  dateSubmitted: string;
  isLoading?: boolean;
  onClick: () => void;
  payoutId: string;
  sentBy: string;
  site?: string;
  status: PayoutStatus;
  timeSubmitted: string;
}

export const UserPayoutRow = ({ alert, gross, net, holdback, autograts, tips, businessDay, isLoading, onClick, payoutId, status, ...rest }: IUserPayoutRowProps) => (
  <Row clickable onClick={onClick} {...rest}>
    <Cell minWidth={110} data-cy="business-day-cell">
      <Column>
        {isLoading ? <StyledSkeleton width={110} /> : <strong>{businessDay}</strong>}
        {isLoading ? <StyledSkeleton width={110} /> : payoutId}
      </Column>
    </Cell>

    <Cell minWidth={110} data-cy="status-cell">
      <StatusPayment
        isLoading={isLoading}
        status={status}
        paymentId={payoutId}
        {...(alert && {
          subStatusLabel: alert.label,
          subStatusTooltip: alert.message,
        })}
      />
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tips-cell">
      {isLoading ? <Skeleton width={110} /> : tips}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="autograts-cell">
      {isLoading ? <Skeleton width={110} /> : autograts}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tip-gross-cell">
      {isLoading ? <Skeleton width={110} /> : gross}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tip-holdback-cell">
      {isLoading ? <Skeleton width={110} /> : holdback}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tip-net-cell">
      {isLoading ? <Skeleton width={110} /> : <strong>{net}</strong>}
    </Cell>
  </Row>
);

export enum ActiveReportTab {
  Prefunded = 'prefunded',
  Acceptance = 'acceptance',
}

export interface IParams {
  option?: string;
}

interface IAcceptanceRowProps extends Loadable {
  date?: string;
  time?: string;
  cardholder?: string;
  trxId?: string;
  employeeName?: string;
  tipAmount?: number;
  fees?: number;
  net?: number;
}

export const AcceptancePaymentRow = ({ date, time, cardholder, trxId, employeeName, tipAmount, fees, net, isLoading, ...rest }: IAcceptanceRowProps) => (
  <Row {...rest}>
    <Cell maxWidth={105} data-cy="timestamp">
      {date && <TableTimestamp align="left" date={date} time={time} isLoading={isLoading} />}
    </Cell>

    <Cell data-cy="cardholder">{isLoading ? <Skeleton width={125} /> : cardholder || '--'}</Cell>

    <Cell align="left" data-cy="transaction-id">
      {isLoading ? <Skeleton width={125} /> : trxId || '--'}
    </Cell>

    <Cell align="left" data-cy="employee-name">
      {isLoading ? <Skeleton width={125} /> : employeeName || '--'}
    </Cell>

    <Cell align="left" data-cy="tip-amount">
      {isLoading ? <Skeleton width={55} /> : centsToDollarsDisplay(tipAmount || 0)}
    </Cell>

    <Cell align="left" data-cy="fees">
      {isLoading ? <Skeleton width={55} /> : centsToDollarsDisplay(fees || 0)}
    </Cell>

    <Cell align="left" data-cy="net-amount">
      <strong>{isLoading ? <Skeleton width={55} /> : centsToDollarsDisplay(net || 0)}</strong>
    </Cell>
  </Row>
);

const getStatusMessage = (status: AchStatus) => {
  switch (status) {
    case AchStatus.APPLIED:
      return 'This ACH is complete and has been applied to the site ledger.';
    case AchStatus.CANCELED:
      return 'This ACH was canceled.';
    case AchStatus.PENDING:
      return 'This ACH will be processed on the next bank business day.';
    case AchStatus.PROCESSED:
      return 'This ACH has been submitted to your bank.';
    case AchStatus.PROCESSING:
      return 'The system is currently working on this ACH.';
    case AchStatus.RETURNED:
      return 'This ACH failed. Click on the ACH to see the failure reason.';
    default:
      return capitalize(status);
  }
};

const Content = styled.div`
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(10)};
`;

interface IAchTransactionRowProps {
  transaction?: IAchTransaction;
  isLoading?: boolean;
  onClick?: () => void;
}

export const AchTransactionRow = ({ transaction, isLoading, onClick, ...rest }: IAchTransactionRowProps) => (
  <Row clickable onClick={onClick} {...rest} data-value={transaction?.id}>
    <Cell maxWidth={120} align="left" data-cy="date-cell">
      {isLoading ? <Skeleton width={100} /> : <b>{dayjs(transaction?.createdAt).format('MM-DD-YYYY')}</b>}
    </Cell>

    <Cell maxWidth={120} align="left" data-cy="id-cell">
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <Content>
          {transaction?.id} {transaction?.achResponse?.type === AchResponseType.CHANGE_REQUEST && <Tag data-cy="noc-message" className="noc-message" appearance={Appearance.WARNING} label="NOC" size={Size.SMALL} />}
        </Content>
      )}
    </Cell>

    <Cell minWidth={170} maxWidth={200} align="left" wrapText data-cy="desc-cell">
      {isLoading ? <Skeleton width={160} /> : transaction?.method === AchMethod.AUTO ? 'Automatic ACH' : transaction?.description || '--'}
    </Cell>

    <Cell align="left" data-cy="type-cell">
      {isLoading ? <Skeleton width={100} /> : capitalize(transaction?.paymentType)}
    </Cell>

    <Cell minWidth={100} data-cy="status-cell">
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <TooltipWrapper data-cy="tooltip-wrapper">
          {capitalize(transaction?.status)}
          <TooltipIcon id={`tooltip-${transaction?.id}`} place="top">
            {getStatusMessage(transaction?.status)}
          </TooltipIcon>
        </TooltipWrapper>
      )}
    </Cell>

    <Cell align="left" data-cy="amount-cell">
      {isLoading ? <Skeleton width={100} /> : <b>{centsToDollarsDisplay(transaction?.amount)}</b>}
    </Cell>
  </Row>
);
